// CompanySelectionModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FiSearch } from "react-icons/fi";
import { MdVerified, MdOutlineKeyboardArrowRight } from "react-icons/md";
import {
  useCreateNewCompanyMutation,
  useLazyGetMedCompanyQuery,
} from "../../State/Api/PharmacyStockApiSlice";

function CompanySelectionModal({ show, onClose, onSelect, initialValue = "" }) {
  // lazy query trigger + result object from RTK Query
  const [triggerGetMedCompany, getMedCompanyResult] = useLazyGetMedCompanyQuery();
  const { data: apiData, isFetching, isError, error } = getMedCompanyResult;

  const [createCompany, createResult] = useCreateNewCompanyMutation();

  // local list of companies to display (derived from apiData)
  const [companies, setCompanies] = useState([]);

  // debounce timer id
  const [searchTerm, setSearchTerm] = useState(initialValue || "");

  // Formik for form handling (could be adjusted if you already use a common form)
  const formik = useFormik({
    initialValues: { medicineCompanyName: initialValue || "" },
    validationSchema: Yup.object({
      medicineCompanyName: Yup.string().required("Company name is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const pharmaId = localStorage.getItem("userUniqueID") || "";
        // call create mutation - adjust payload shape to your API
        await createCompany({ pharmaId, companyName: values.medicineCompanyName }).unwrap();
        setSubmitting(false);
        onSelect?.(values.medicineCompanyName);
        onClose();
      } catch (err) {
        console.error("Create company failed", err);
        setSubmitting(false);
      }
    },
  });

  // Derive a normalized array of company names from the returned apiData.
  // The shape of apiData may vary — handle common shapes gracefully.
  useEffect(() => {
    if (!apiData) {
      setCompanies([]);
      return;
    }

    // Try common locations for the list
    let list = [];
    if (Array.isArray(apiData)) {
      list = apiData;
    } else if (Array.isArray(apiData.data)) {
      list = apiData.data;
    } else if (Array.isArray(apiData.result)) {
      list = apiData.result;
    } else if (Array.isArray(apiData.medicineCompanyList)) {
      list = apiData.medicineCompanyList;
    } else if (Array.isArray(apiData.medicineCompanies)) {
      list = apiData.medicineCompanies;
    } else {
      // last-ditch: try to collect values if apiData is an object map
      list = Object.values(apiData).flat().filter(Boolean);
    }

    // Normalize to array of strings (companyName property or string values)
    const normalized = list.map((item) => {
      if (!item) return null;
      if (typeof item === "string") return item;
      // common field names
      if (item.companyName) return item.companyName;
      if (item.medicineCompanyName) return item.medicineCompanyName;
      if (item.name) return item.name;
      // fallback to JSON string
      return typeof item === "object" ? JSON.stringify(item) : String(item);
    }).filter(Boolean);

    // remove duplicates while preserving order
    const deduped = Array.from(new Set(normalized));
    setCompanies(deduped);
  }, [apiData]);

  // Debounce the searchTerm -> trigger API call after 350ms
  useEffect(() => {
    const pharmaId = localStorage.getItem("userUniqueID") || "";
    const value = searchTerm?.trim();

    if (!value) {
      // if empty, clear results
      setCompanies([]);
      return;
    }

    const timer = setTimeout(() => {
      // IMPORTANT: trigger accepts the single argument object as assumed by this code
      // If your endpoint expects an array, do: triggerGetMedCompany([pharmaId, value])
      triggerGetMedCompany({ pharmaId, medCompany: value });
    }, 350);

    return () => clearTimeout(timer);
  }, [searchTerm, triggerGetMedCompany]);

  // Wire input changes to both formik and local debounced state
  const handleInputChange = (e) => {
    const val = e.target.value;
    formik.setFieldValue("medicineCompanyName", val);
    setSearchTerm(val);
  };

  const handleSelectCompany = (company) => {
    formik.setFieldValue("medicineCompanyName", company);
    onSelect?.(company);
    onClose();
  };

  // filtered list based on typed input (optional)
  const filtered = useMemo(() => {
    const q = formik.values.medicineCompanyName.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((c) => c.toLowerCase().includes(q));
  }, [companies, formik.values.medicineCompanyName]);

  return (
    <Modal show={show} onHide={onClose} centered>
      <form onSubmit={formik.handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Select / Create Company</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-3">
            <label className="form-label small text-muted">Company</label>
            <div className="position-relative">
              <FiSearch
                className="position-absolute"
                style={{ left: 10, top: 11, color: "#888" }}
              />
              <input
                name="medicineCompanyName"
                placeholder="Search company or type a new name..."
                className="form-control"
                style={{ paddingLeft: 32 }}
                value={formik.values.medicineCompanyName}
                onChange={handleInputChange}
                autoComplete="off"
              />
            </div>
            {formik.touched.medicineCompanyName && formik.errors.medicineCompanyName && (
              <div className="text-danger small mt-1">{formik.errors.medicineCompanyName}</div>
            )}
          </div>

          {/* Loading / error */}
          {isFetching && (
            <div className="small text-muted mb-2">Searching companies...</div>
          )}


          {/* List header */}
          {filtered.length > 0 && (
            <div className="mt-2 small text-muted">COMPANY NAME</div>
          )}

          {/* Scrollable company list */}
          <div
            className="mt-1"
            style={{
              maxHeight: 220,
              overflowY: "auto",
              borderRadius: 8,
            }}
          >
            {filtered.length > 0 ? (
              filtered.map((c, idx) => (
                <div
                  key={c + idx}
                  className="d-flex align-items-center justify-content-between py-2 px-2"
                  style={{
                    borderBottom: "1px solid #eee",
                    cursor: "pointer",
                  }}
                  onClick={() =>{ console.log(c); handleSelectCompany(c); }}
                >
                  <div className="d-flex align-items-center" style={{ gap: 12 }}>
                    <div style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 600,
                      color: "#333"
                    }}>
                      {c.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 14 }}>{c}</div>
                      {/* optional subtitle: show verified icon if you need */}
                    </div>
                  </div>

                  <div>
                    <MdOutlineKeyboardArrowRight size={20} color="#888" />
                  </div>
                </div>
              ))
            ) : (
              <div className="text-muted small p-2">No matching companies found.</div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light"
            onClick={onClose}
            disabled={formik.isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-dark"
            disabled={formik.isSubmitting || filtered.length > 0}
          >
            {formik.isSubmitting ? "Saving..." : "Save Company"}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

export default CompanySelectionModal;
